import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { api } from "@/lib/api";
import { useOrg } from "@/lib/org-context";
import {
  claimSourceConnectPrompt,
  forceClaimSourceConnectPrompt,
  isAnySourceConnectPromptOpen,
  onSourceConnectPromptClosed,
  releaseSourceConnectPrompt,
} from "./source-connect-prompt-guard";

const DISMISS_KEY = "as-zoom-dismiss";
const DISMISS_FOREVER_KEY = "as-zoom-dismiss-forever";

export function openZoomConsent() {
  sessionStorage.removeItem(DISMISS_KEY);
  localStorage.removeItem(DISMISS_FOREVER_KEY);
  window.dispatchEvent(new CustomEvent("as-zoom-prompt"));
}

/**
 * Explains exactly what Zoom's consent screen will ask for before
 * redirecting to Zoom OAuth. Mirrors the Google calendar pre-consent:
 * auto-opens when no active Zoom connection exists, and opens on demand
 * when the user clicks Connect on the Zoom card.
 */
export function ZoomConnectPrompt() {
  const { orgId, canManage, organization } = useOrg();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId || !canManage) return;

    async function maybeOpen(force = false) {
      if (
        !force &&
        (sessionStorage.getItem(DISMISS_KEY) === "1" ||
          localStorage.getItem(DISMISS_FOREVER_KEY) === "1")
      ) {
        return;
      }
      if (!force && isAnySourceConnectPromptOpen()) return;
      try {
        const zoom = await api<{
          connections: Array<{ status: string }>;
        }>(`/api/accounts/${orgId}/sources/zoom`).catch(() => ({
          connections: [] as Array<{ status: string }>,
        }));
        const hasActive = zoom.connections.some((c) => c.status === "active");
        if (hasActive) return;
        if (!force && !claimSourceConnectPrompt()) return;
        if (force) forceClaimSourceConnectPrompt();
        setError(null);
        setOpen(true);
      } catch {
        /* ignore */
      }
    }

    void maybeOpen(false);
    const onForce = () => void maybeOpen(true);
    const onClosed = () => void maybeOpen(false);
    window.addEventListener("as-zoom-prompt", onForce);
    const removeClosed = onSourceConnectPromptClosed(onClosed);
    return () => {
      window.removeEventListener("as-zoom-prompt", onForce);
      removeClosed();
    };
  }, [orgId, canManage]);

  function close(release = true) {
    setOpen(false);
    setBusy(false);
    if (release) releaseSourceConnectPrompt();
  }

  function skip() {
    sessionStorage.setItem(DISMISS_KEY, "1");
    close();
  }

  function dismissForever() {
    localStorage.setItem(DISMISS_FOREVER_KEY, "1");
    sessionStorage.removeItem(DISMISS_KEY);
    close();
  }

  async function continueToZoom() {
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ connectUrl: string }>(
        `/api/accounts/${orgId}/sources/zoom/connect-link`,
        {
          method: "POST",
          body: JSON.stringify({
            hostLabel: organization?.name ?? "Zoom",
            returnPath: "/integrations",
          }),
        },
      );
      window.location.href = result.connectUrl;
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Could not start Zoom connection",
      );
      setBusy(false);
    }
  }

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) skip();
        else setOpen(true);
      }}
      title="Connect Zoom"
      description="Zoom will ask you to allow AttendeeSync to access your account. You can revoke access anytime in the Zoom Marketplace under Installed Apps."
      impact={
        <div className="space-y-2">
          <ul className="list-disc space-y-1 pl-4 text-sm text-rs-ink">
            <li>See your Zoom profile (already used for sign-in)</li>
            <li>View your meetings and webinars and their registrants</li>
            <li>View registration questions, including custom answers</li>
            <li>Receive new-registration events to trigger syncs</li>
            <li>
              AttendeeSync will use this to discover events and create sync
              streams when Auto-Create is enabled
            </li>
          </ul>
          {error ? (
            <p role="alert" className="text-sm text-rs-danger">
              {error}
            </p>
          ) : null}
        </div>
      }
      contentClassName="w-[min(37rem,calc(100vw-2rem))]"
      cancelLabel="Skip"
      footerLeft={
        <Button
          type="button"
          variant="link"
          className="text-rs-danger"
          disabled={busy}
          onClick={dismissForever}
        >
          Dismiss Forever
        </Button>
      }
      confirmLabel={busy ? "Redirecting…" : "Continue To Zoom"}
      busy={busy}
      onConfirm={() => void continueToZoom()}
    />
  );
}
